/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Grid,
  Stack,
  Typography,
  Card,
  CardContent,
  useMediaQuery,
  SvgIcon,
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  getFormattedDateShortMonthDDYYYY,
  OutlinedIcons,
  CustomIcons,
  OtherChips,
  TravelerTagChips,
} from '@marageti/z4-lib';
import {
  ConversationType, TripMatchStatus, TripMessageState, TripRequestStatus,
} from '@marageti/z4-sdk/lib/travel';
import { TravelerTags } from '@marageti/z4-sdk/lib/people';
import { TripMatchStatusChip, TripRequestStatusChip } from '../../../../components/list-cards/chips';
import { ChatIndicator } from '../../../../components/list-cards/trip-indicators';
import ClearFromQueueButton from './clear-from-queue';
import LastMessageTimestampChip from './timestamp-chip';
import { useAuthStore } from '../../../../store';

const {
  OutlinedLocationMarkerIcon,
} = OutlinedIcons;

const {
  DepartureIcon,
} = CustomIcons;

const IconLabel = ({ icon, label }: { icon: any, label: any }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <SvgIcon color="disabled" component={icon} />
    <Typography variant="caption">
      {label}
    </Typography>
  </Stack>
);
const {
  ChipRepeat,
} = OtherChips;
const {
  ChipVIP,
  ChipFOBY,
} = TravelerTagChips;

type InboxItemCardProps = {
  to: string;
  type: ConversationType.ZicassoToPartner | ConversationType.TravelerToZicasso;
  status: TripMatchStatus | TripRequestStatus | undefined;
  travelerName: string;
  partnerName?: string;
  firstUnreadAt: string;
  onClearFromQueue: (e: React.MouseEvent<HTMLButtonElement>) => void;
  travelerTags: string[];
  departureDate: string;
  createdAt: string;
  tripMessageState: TripMessageState;
  destinations: string;
};

const InboxItemCardView = ({
  to,
  type,
  travelerName,
  partnerName,
  status,
  firstUnreadAt,
  onClearFromQueue,
  travelerTags,
  departureDate,
  createdAt,
  tripMessageState,
  destinations,
}: InboxItemCardProps) => {
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('desktop'));

  const formattedDepartureDate = getFormattedDateShortMonthDDYYYY(departureDate) || '';
  const isPartnerInbox = type === ConversationType.ZicassoToPartner;
  const { loggedInTeamMember } = useAuthStore();
  const userId = loggedInTeamMember?.id || '';

  return (
    <Card
      component={Link}
      sx={{
        '&:hover': {
          bgcolor: 'grey.A100',
        },
        textDecoration: 'none',
      }}
      to={to}
    >
      <CardContent>
        {isMobile ? (
          <Stack direction="column" spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="bodyBold">
                {isPartnerInbox ? `${travelerName}'s trip` : travelerName}
              </Typography>
              {travelerTags.map((tag) => {
                switch (tag) {
                  case 'VIP':
                    return <ChipVIP key={`${travelerName}-${tag}-tag`} />;
                  case 'Repeat':
                    return <ChipRepeat key={`${travelerName}-${tag}-tag`} />;
                  case TravelerTags.FOBY:
                    return <ChipFOBY key={`${travelerName}-${tag}-tag`} />;
                  default:
                    return null;
                }
              })}
              <Typography variant="caption" alignSelf="center">
                {isPartnerInbox && partnerName}
              </Typography>
            </Stack>

            <Stack direction="column" spacing={1}>
              <IconLabel icon={OutlinedLocationMarkerIcon} label={destinations} />
              <IconLabel
                icon={DepartureIcon}
                label={`Departure: ${formattedDepartureDate}`}
              />
            </Stack>

            <Grid
              container
              alignItems="center"
            >
              <Grid item xs={6} display="flex">
                {isPartnerInbox ? <TripMatchStatusChip status={status as TripMatchStatus} /> : <TripRequestStatusChip status={status as TripRequestStatus} />}
              </Grid>
              <Grid item xs={6} display="flex" justifyContent="flex-end">
                <Stack gap={1.5} alignItems="center" flexDirection="row-reverse">
                  <span style={{ position: 'relative', top: '3px' }}>
                    <ChatIndicator
                      assignedTo={userId}
                      createdAt={createdAt}
                      tripMessageState={tripMessageState}
                    />
                  </span>
                  <ClearFromQueueButton clearFromQueue={onClearFromQueue} />
                  <LastMessageTimestampChip timestamp={firstUnreadAt} />
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        ) : (
          <Grid container direction="row" alignItems="center" columnSpacing={1.5}>
            <Grid item xs={4}>
              <Stack direction="column" spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="bodyBold">
                    {isPartnerInbox ? `${travelerName}'s trip` : travelerName}
                  </Typography>
                  {travelerTags.map((tag) => {
                    switch (tag) {
                      case 'VIP':
                        return <ChipVIP key={`${travelerName}-${tag}-tag`} />;
                      case 'Repeat':
                        return <ChipRepeat key={`${travelerName}-${tag}-tag`} />;
                      case TravelerTags.FOBY:
                        return <ChipFOBY key={`${travelerName}-${tag}-tag`} />;
                      default:
                        return null;
                    }
                  })}
                </Stack>
                <Typography variant="caption">
                  {isPartnerInbox && partnerName}
                </Typography>
              </Stack>
            </Grid>

            <Grid item xs={2.75}>
              <Typography variant="body" sx={!isPartnerInbox ? { textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' } : {}}>
                {destinations}
              </Typography>
            </Grid>

            <Grid item xs={1.8}>
              <Typography variant="body">
                {formattedDepartureDate}
              </Typography>
            </Grid>

            <Grid item xs={2.2}>
              <Stack direction="row" gap={1.5} alignItems="center">
                <span style={{ position: 'relative', top: '3px' }}>
                  <ChatIndicator
                    assignedTo={userId}
                    createdAt={createdAt}
                    tripMessageState={tripMessageState}
                  />
                </span>
                <ClearFromQueueButton clearFromQueue={onClearFromQueue} />
                <LastMessageTimestampChip timestamp={firstUnreadAt} />
              </Stack>

            </Grid>
            <Grid item xs={1.25} display="flex" justifyContent="flex-end" alignItems="center" minWidth={100}>
              {isPartnerInbox ? <TripMatchStatusChip status={status as TripMatchStatus} /> : <TripRequestStatusChip status={status as TripRequestStatus} />}
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default InboxItemCardView;
